import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Minus, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartDrawer = () => {
  const { items, setQty, removeItem, clear, total, isOpen, setIsOpen } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/preorders`, {
        ...form,
        items: items.map((i) => ({ product_id: i.id, name: i.name, qty: i.qty, unit: i.unit, price: i.price })),
      });
      setConfirmed(data);
      clear();
      toast.success("Pre-order received", { description: "We'll confirm your harvest window by email." });
    } catch {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  const close = (open) => {
    setIsOpen(open);
    if (!open) setTimeout(() => setConfirmed(null), 400);
  };

  return (
    <Sheet open={isOpen} onOpenChange={close}>
      <SheetContent
        data-testid="cart-drawer"
        className="bg-surface border-line w-full sm:max-w-lg overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-3xl text-zinc-100 tracking-tight">
            Your Basket
          </SheetTitle>
        </SheetHeader>

        {confirmed ? (
          <div data-testid="preorder-confirmation" className="mt-16 flex flex-col items-start gap-6">
            <CheckCircle2 className="w-12 h-12 text-saffron" />
            <p className="font-display text-3xl text-zinc-100">The hog has your order.</p>
            <p className="text-zinc-400 leading-relaxed">
              Order <span className="text-saffron font-mono text-sm">{confirmed.id.slice(0, 8)}</span> —
              totalling <span className="text-zinc-100">${confirmed.total.toFixed(2)}</span>. We'll email
              you at <span className="text-zinc-100">{confirmed.email}</span> to arrange pickup at the
              farm gate in Mount Albert.
            </p>
            <button
              data-testid="preorder-continue-button"
              onClick={() => close(false)}
              className="mt-4 rounded-full bg-saffron px-8 py-3 text-xs uppercase tracking-[0.2em] font-bold text-obsidian hover:bg-saffron-hover transition-colors focus-ring"
            >
              Back to the farm
            </button>
          </div>
        ) : items.length === 0 ? (
          <div data-testid="cart-empty-state" className="mt-24 text-zinc-500">
            <p className="font-display text-2xl text-zinc-300 mb-3">An empty basket is a quiet field.</p>
            <p className="text-sm leading-relaxed">Wander the harvest and add what calls to you.</p>
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-8">
            <ul className="flex flex-col divide-y divide-line">
              {items.map((i) => (
                <li key={i.id} data-testid={`cart-item-${i.id}`} className="py-5 flex gap-4">
                  <img src={i.image} alt={i.name} className="w-16 h-16 object-cover rounded-md border border-line" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg text-zinc-100 leading-tight">{i.name}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      ${i.price.toFixed(2)} / {i.unit}
                    </p>
                    <div className="flex items-center gap-3 mt-3">
                      <button
                        data-testid={`cart-qty-minus-${i.id}`}
                        onClick={() => setQty(i.id, i.qty - 1)}
                        className="w-7 h-7 rounded-full border border-line flex items-center justify-center text-zinc-400 hover:border-saffron hover:text-saffron transition-colors focus-ring"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span data-testid={`cart-qty-${i.id}`} className="text-sm w-8 text-center text-zinc-200">
                        {i.qty}
                      </span>
                      <button
                        data-testid={`cart-qty-plus-${i.id}`}
                        onClick={() => setQty(i.id, i.qty + 1)}
                        className="w-7 h-7 rounded-full border border-line flex items-center justify-center text-zinc-400 hover:border-saffron hover:text-saffron transition-colors focus-ring"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <span className="ml-auto text-sm text-zinc-300">${(i.qty * i.price).toFixed(2)}</span>
                      <button
                        data-testid={`cart-remove-${i.id}`}
                        onClick={() => removeItem(i.id)}
                        className="text-zinc-600 hover:text-wine-light transition-colors focus-ring"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex items-center justify-between border-t border-line pt-6">
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Estimated Total</span>
              <span data-testid="cart-total" className="font-display text-3xl text-saffron">
                ${total.toFixed(2)}
              </span>
            </div>

            <form data-testid="preorder-form" onSubmit={submit} className="flex flex-col gap-4 pb-8">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Pre-order Details</p>
              <input
                data-testid="preorder-name-input"
                required
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-obsidian border border-line rounded-md px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus-ring"
              />
              <input
                data-testid="preorder-email-input"
                required
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-obsidian border border-line rounded-md px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus-ring"
              />
              <input
                data-testid="preorder-phone-input"
                placeholder="Phone (optional)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-obsidian border border-line rounded-md px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus-ring"
              />
              <textarea
                data-testid="preorder-notes-input"
                placeholder="Notes — pickup timing, substitutions, wholesale interest…"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-obsidian border border-line rounded-md px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus-ring resize-none"
              />
              <button
                data-testid="preorder-submit-button"
                disabled={submitting}
                className="mt-2 rounded-full bg-saffron px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold text-obsidian hover:bg-saffron-hover transition-colors disabled:opacity-50 focus-ring"
              >
                {submitting ? "Sending to the farm…" : "Place Pre-order"}
              </button>
              <p className="text-xs text-zinc-600 leading-relaxed">
                No payment now. We confirm availability and your harvest window by email — pay at pickup.
              </p>
            </form>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
